import { buildRevenueColumns } from "../../../budget/model/columns/columnBuilders.js";
import { sumSectionTotals } from "../../../budget/utils/programBudgetUtils.js";
import { aggregateFromSections } from "../../townBudget/utils/summaryOfRevenuesAndExpendituresUtils.js";

const TOWN_GROUP_LABELS = {
  town: "Town",
  debtServiceAndTransfers: "Debt Service & Transfers Out",
  education: "Education",
};

function roundAverageCurrency(value) {
  return Number.isFinite(value) ? Math.round(value) : value;
}

function toVarianceRow({ label, group, approved, actual, fiscalYear }) {
  if (!Number.isFinite(approved) || !Number.isFinite(actual)) return null;

  const variance = actual - approved;

  return {
    id: `${group}:${label}:${fiscalYear}`,
    label,
    group,
    fiscalYear,
    approved,
    actual,
    variance,
    varianceAbs: Math.abs(variance),
    variancePct: approved !== 0 ? variance / approved : null,
    direction:
      variance > 0 ? "Over budget" : variance < 0 ? "Under budget" : "On budget",
  };
}

export function buildTownVarianceRows({ comparisonYear, groupedSections }) {
  if (!Number.isFinite(comparisonYear)) return [];

  const { columns } = buildRevenueColumns(comparisonYear + 2);

  return Object.entries(groupedSections || {})
    .flatMap(([groupKey, sections]) => {
      const groupLabel = TOWN_GROUP_LABELS[groupKey] || groupKey;

      return (sections || [])
        .map((section) => {
          const label =
            section?.title ||
            section?.name ||
            section?.label ||
            section?.sectionTitle ||
            groupLabel;

          const aggregate = aggregateFromSections([section], columns, label);

          return toVarianceRow({
            label,
            group: groupLabel,
            approved: aggregate?.y2_adopted,
            actual: aggregate?.y2_actual,
            fiscalYear: comparisonYear,
          });
        })
        .filter(Boolean);
    })
    .sort((a, b) => b.varianceAbs - a.varianceAbs || a.label.localeCompare(b.label));
}

export function buildBoeVarianceRows({ comparisonYear, programDocs, programConfig }) {
  if (!Number.isFinite(comparisonYear)) return [];

  return (programConfig || [])
    .map((cfg) => {
      const yearNode = programDocs?.[cfg.docId]?.[String(comparisonYear)];
      if (!yearNode) return null;

      return toVarianceRow({
        label: cfg.label,
        group: "Board of Education",
        approved: sumSectionTotals(yearNode?.Approved),
        actual: sumSectionTotals(yearNode?.Actual),
        fiscalYear: comparisonYear,
      });
    })
    .filter(Boolean)
    .sort((a, b) => b.varianceAbs - a.varianceAbs || a.label.localeCompare(b.label));
}

export function getComparableTownYears(rawDocsByGroup) {
  const yearSet = new Set();

  Object.values(rawDocsByGroup || {}).forEach((docData) => {
    Object.entries(docData || {}).forEach(([yearKey, yearNode]) => {
      const year = Number(yearKey);
      if (!Number.isFinite(year)) return;

      const hasApproved = !!(yearNode?.Approved || yearNode?.Adopted);
      const hasActual = !!yearNode?.Actual;

      if (hasApproved && hasActual) yearSet.add(year);
    });
  });

  return Array.from(yearSet).sort((a, b) => a - b);
}

export function getComparableBoeYears(programDocs) {
  const yearSet = new Set();

  Object.values(programDocs || {}).forEach((docData) => {
    Object.entries(docData || {}).forEach(([yearKey, yearNode]) => {
      const year = Number(yearKey);
      if (!Number.isFinite(year)) return;

      if (yearNode?.Approved && yearNode?.Actual) yearSet.add(year);
    });
  });

  return Array.from(yearSet).sort((a, b) => a - b);
}

export function buildAverageVarianceRows(rowsByYear, years) {
  const targetYears = Array.isArray(years) ? years.filter(Number.isFinite) : [];
  if (!targetYears.length) return [];

  const buckets = new Map();

  targetYears.forEach((year) => {
    const rows = Array.isArray(rowsByYear?.[year]) ? rowsByYear[year] : [];

    rows.forEach((row) => {
      const key = `${row.group}::${row.label}`;
      const existing =
        buckets.get(key) ||
        {
          id: key,
          label: row.label,
          group: row.group,
          approvedSum: 0,
          actualSum: 0,
          varianceSum: 0,
          variancePctSum: 0,
          variancePctCount: 0,
          yearsIncluded: 0,
        };

      existing.approvedSum += row.approved;
      existing.actualSum += row.actual;
      existing.varianceSum += row.variance;
      if (typeof row.variancePct === "number" && Number.isFinite(row.variancePct)) {
        existing.variancePctSum += row.variancePct;
        existing.variancePctCount += 1;
      }
      existing.yearsIncluded += 1;

      buckets.set(key, existing);
    });
  });

  return Array.from(buckets.values())
    .map((bucket) => {
      const avgVariance = bucket.varianceSum / bucket.yearsIncluded;

      return {
        id: bucket.id,
        label: bucket.label,
        group: bucket.group,
        yearsIncluded: bucket.yearsIncluded,
        approved: roundAverageCurrency(bucket.approvedSum / bucket.yearsIncluded),
        actual: roundAverageCurrency(bucket.actualSum / bucket.yearsIncluded),
        variance: roundAverageCurrency(avgVariance),
        varianceAbs: Math.abs(avgVariance),
        variancePct:
          bucket.variancePctCount > 0
            ? bucket.variancePctSum / bucket.variancePctCount
            : null,
        direction:
          avgVariance > 0
            ? "Avg over budget"
            : avgVariance < 0
              ? "Avg under budget"
              : "Avg on budget",
      };
    })
    .sort((a, b) => b.varianceAbs - a.varianceAbs || a.label.localeCompare(b.label));
}
